/**
 * Shared state for the currently-active Try-It request.
 *
 * Sidebar / detail page write to it; the right panel reads from it to render
 * live cURL and the last response. Decoupling via store means the right panel
 * doesn't need prop drilling through the layout.
 *
 * Lightweight subscriber store — same shape as bo-chat3 frontend uses; we
 * just typed it. No external dep (zustand, jotai) needed.
 */

import { useSyncExternalStore } from 'react';
import type { EndpointInfo, RequestResult } from '@/api/client';

export type BodyMode = 'form' | 'raw';

/**
 * User-defined custom field row — pakai pas endpoint TIDAK punya documented
 * schema (sebagian besar endpoint backend belum di-JSDoc). Kita tetep kasih
 * UX form (key/value rows + "Add field" button) bukannya raw JSON textarea
 * supaya konsisten dengan endpoint yang schema-nya udah lengkap.
 *
 *   id     — stable React key (gak ikut serialize)
 *   name   — JSON key yang user ketik
 *   value  — string (akan di-coerce ke number/boolean kalau format obvious)
 */
export interface CustomBodyField {
  id: string;
  name: string;
  value: string;
}

export interface TryItState {
  endpoint: EndpointInfo | null;
  pathParams: Record<string, string>;
  queryParams: Record<string, string>;
  headerParams: Record<string, string>;
  /** Raw JSON text (used directly when bodyMode === 'raw') */
  body: string;
  /** Per-field structured values (used when bodyMode === 'form'). Schema-defined. */
  bodyFields: Record<string, unknown>;
  /** User-added rows untuk endpoint tanpa schema. Merged ke bodyFields saat submit. */
  customBodyFields: CustomBodyField[];
  /** UI mode for editing the body — 'form' = type-aware inputs, 'raw' = JSON textarea */
  bodyMode: BodyMode;
  result: RequestResult | null;
  isLoading: boolean;
  error: string | null;
}

const initial: TryItState = {
  endpoint: null,
  pathParams: {},
  queryParams: {},
  headerParams: {},
  body: '',
  bodyFields: {},
  customBodyFields: [],
  bodyMode: 'form',
  result: null,
  isLoading: false,
  error: null,
};

let state: TryItState = initial;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot() {
  return state;
}

function emit() {
  for (const l of listeners) l();
}

export function setEndpoint(endpoint: EndpointInfo | null) {
  // Reset form when endpoint changes
  state = {
    ...initial,
    endpoint,
  };
  emit();
}

export function setPathParam(name: string, value: string) {
  state = { ...state, pathParams: { ...state.pathParams, [name]: value } };
  emit();
}

export function setQueryParam(name: string, value: string) {
  state = { ...state, queryParams: { ...state.queryParams, [name]: value } };
  emit();
}

export function setHeaderParam(name: string, value: string) {
  state = { ...state, headerParams: { ...state.headerParams, [name]: value } };
  emit();
}

export function setBody(body: string) {
  state = { ...state, body };
  emit();
}

/** Set a single field in the structured body. Triggers re-render of dependent UI. */
export function setBodyField(name: string, value: unknown) {
  state = { ...state, bodyFields: { ...state.bodyFields, [name]: value } };
  emit();
}

/** Replace the entire structured body (used pas endpoint berubah / Reset example). */
export function setBodyFields(bodyFields: Record<string, unknown>) {
  state = { ...state, bodyFields };
  emit();
}

/** Switch between form & raw JSON editing modes. */
export function setBodyMode(bodyMode: BodyMode) {
  state = { ...state, bodyMode };
  emit();
}

/** Append a fresh empty custom field row. */
export function addCustomBodyField() {
  const id = `cf_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  state = {
    ...state,
    customBodyFields: [...state.customBodyFields, { id, name: '', value: '' }],
  };
  emit();
}

/** Update one custom field row (partial). */
export function updateCustomBodyField(id: string, patch: Partial<Omit<CustomBodyField, 'id'>>) {
  state = {
    ...state,
    customBodyFields: state.customBodyFields.map((f) =>
      f.id === id ? { ...f, ...patch } : f
    ),
  };
  emit();
}

/** Remove one custom field row by id. */
export function removeCustomBodyField(id: string) {
  state = {
    ...state,
    customBodyFields: state.customBodyFields.filter((f) => f.id !== id),
  };
  emit();
}

/** Replace entire custom field list (used pas endpoint berubah). */
export function setCustomBodyFields(fields: CustomBodyField[]) {
  state = { ...state, customBodyFields: fields };
  emit();
}

export function setResult(result: RequestResult | null) {
  state = { ...state, result, isLoading: false, error: null };
  emit();
}

export function setLoading(isLoading: boolean) {
  state = { ...state, isLoading, error: isLoading ? null : state.error };
  emit();
}

export function setError(error: string | null) {
  state = { ...state, error, isLoading: false };
  emit();
}

export function useTryIt(): TryItState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
